'use client';

import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ModalRenderProps } from 'react-aria-components';

export type ModalTransitionStyle = 'fade' | 'none';

export type ModalStackPresentation = {
    isRoot: boolean;
    isCovered: boolean;
    layersAbove: number;
    transitionStyle: ModalTransitionStyle;
};

type ModalStackContextValue = ModalStackPresentation & {
    familyDepth: number;
    isOpen: boolean;
    isReady: boolean;
    reportOpenChange: (delta: number) => void;
};

type ModalStackLayer = {
    parent: ModalStackContextValue | null;
    familyDepth: number;
    isRoot: boolean;
    layersAbove: number;
    reportOpenChange: (delta: number) => void;
};

const ModalStackContext = React.createContext<ModalStackContextValue | null>(null);

/** Counts open descendants through React ancestry, including across portals. */
export const useModalStackLayer = (): ModalStackLayer => {
    const parent = useContext(ModalStackContext);
    const [layersAbove, setLayersAbove] = useState(0);
    const reportToParent = parent?.reportOpenChange;

    /** Reports a descendant's logical open change to every ancestor. */
    const reportOpenChange = useCallback(
        (delta: number) => {
            setLayersAbove((count) => count + delta);
            reportToParent?.(delta);
        },
        [reportToParent],
    );

    return {
        parent,
        familyDepth: (parent?.familyDepth ?? -1) + 1,
        isRoot: !parent,
        layersAbove,
        reportOpenChange,
    };
};

/** Separates logical-open counting from React Aria's exit presence. */
export const ModalStackLayerProvider: React.FC<{
    children: React.ReactNode;
    layer: ModalStackLayer;
    state: ModalRenderProps['state'];
    transitionStyle: ModalTransitionStyle;
}> = ({ children, layer, state, transitionStyle }) => {
    const { parent, familyDepth, isRoot, layersAbove, reportOpenChange } = layer;
    const { isOpen, close } = state;
    const reportToParent = parent?.reportOpenChange;
    const latestState = useRef(state);
    const [isReady, setIsReady] = useState(false);

    // The follow-up render runs after the whole passive-effect flush, including React Aria's parent setup.
    useEffect(() => {
        // oxlint-disable-next-line react/set-state-in-effect -- Synchronises with React Aria's overlay effects.
        setIsReady(true);
    }, []);

    useLayoutEffect(() => {
        latestState.current = state;
    });

    useLayoutEffect(() => {
        if (!isOpen || !reportToParent) return;
        reportToParent(1);
        return () => reportToParent(-1);
    }, [isOpen, reportToParent]);

    useLayoutEffect(() => {
        if (parent?.isOpen === false && isOpen) close();
    }, [parent?.isOpen, isOpen, close]);

    // Navigation can remove the family without first rendering a closed parent.
    useLayoutEffect(
        () => () => {
            if (!isRoot && latestState.current.isOpen) latestState.current.close();
        },
        [isRoot],
    );

    return (
        <ModalStackContext.Provider
            value={{
                familyDepth,
                isRoot,
                isOpen,
                isReady,
                layersAbove,
                isCovered: layersAbove > 0,
                reportOpenChange,
                transitionStyle,
            }}
        >
            {children}
        </ModalStackContext.Provider>
    );
};

ModalStackLayerProvider.displayName = 'ModalStackLayerProvider';

/** Returns the current layer's presentation, or null for standalone dialog content. */
export const useModalStackPresentation = (): ModalStackPresentation | null => useContext(ModalStackContext);
